// Normalized OLT reasoning for the OLT pages: one state model and one set of
// ONT/PON/optical rules for every vendor. Pure, so
// `bun run src/lib/olthealth.check.ts` can check it.
import { classifyRx, type SignalBands } from "./signal";
import type { ONT } from "./types";

/**
 * Reachability is ONE input, not the verdict:
 *  DOWN     the OLT does not answer.
 *  STALE    it answered once, but not within three polls: numbers are old.
 *  UNKNOWN  never polled, or the collector has no state for it.
 *  CRITICAL a PON is dark, or a quarter of its ONTs are offline.
 *  DEGRADED reachable, but ONTs are offline, the CLI parse was partial, or it runs hot.
 *  HEALTHY  none of the above. Weak optics are warnings, not degradation.
 */
export type OltState = "healthy" | "degraded" | "critical" | "down" | "stale" | "unknown";
export type Reason = "unreachable" | "neverPolled" | "stale" | "ponDown" | "manyOffline" | "offline" | "partial" | "hot";

export const STALE_POLLS = 3;
/** Share of offline ONTs that makes an OLT critical even with every PON lit. */
export const CRITICAL_SHARE = 0.25;
export const HOT_C = 60;

export type OltInput = {
  status: string;
  last_polled_at: string | null;
  onts_total: number;
  onts_online: number;
  temp_c: number | null;
};
export type PonInput = { onts_total: number; onts_online: number };

export function oltState(o: OltInput, pons: PonInput[], pollS: number, nowMs: number): { state: OltState; reasons: Reason[] } {
  if (o.status === "unreachable") return { state: "down", reasons: ["unreachable"] };
  if (!o.last_polled_at || o.status === "unknown") return { state: "unknown", reasons: ["neverPolled"] };
  if ((nowMs - Date.parse(o.last_polled_at)) / 1000 > pollS * STALE_POLLS) return { state: "stale", reasons: ["stale"] };
  const reasons: Reason[] = [];
  const offline = o.onts_total - o.onts_online;
  if (pons.some((p) => p.onts_total >= 2 && p.onts_online === 0)) reasons.push("ponDown");
  if (o.onts_total > 0 && offline / o.onts_total >= CRITICAL_SHARE) reasons.push("manyOffline");
  if (reasons.length) return { state: "critical", reasons };
  if (offline > 0) reasons.push("offline");
  if (o.status === "degraded") reasons.push("partial");
  if (o.temp_c !== null && o.temp_c >= HOT_C) reasons.push("hot");
  return { state: reasons.length ? "degraded" : "healthy", reasons };
}

// --- ONTs --------------------------------------------------------------------

export type OntProblem = { sev: "crit" | "warn"; kind: "offline" | "missing" | "rxCrit" | "rxWarn" };

/** Why an ONT belongs on the "abnormal" list, or null when it is fine. */
export function ontProblem(o: Pick<ONT, "run_state" | "rx_power_dbm">, bands: SignalBands): OntProblem | null {
  if (o.run_state === "Missing") return { sev: "crit", kind: "missing" };
  if (o.run_state !== "Online") return { sev: "crit", kind: "offline" };
  const rx = classifyRx(o.rx_power_dbm, bands);
  if (rx === "crit") return { sev: "crit", kind: "rxCrit" };
  if (rx === "warn") return { sev: "warn", kind: "rxWarn" };
  return null;
}

/** Abnormal first: offline/missing, then critical RX, then weak RX; worst RX first within optics. */
export function abnormalONTs<T extends Pick<ONT, "run_state" | "rx_power_dbm" | "last_down_at" | "last_seen_at">>(
  onts: T[], bands: SignalBands,
): (T & { problem: OntProblem })[] {
  const rank = { offline: 0, missing: 1, rxCrit: 2, rxWarn: 3 };
  return onts
    .map((o) => ({ ...o, problem: ontProblem(o, bands) }))
    .filter((o): o is T & { problem: OntProblem } => o.problem !== null)
    .sort((a, b) =>
      rank[a.problem.kind] - rank[b.problem.kind] ||
      // Offline: the most recent drop first (it is the one being worked).
      (Date.parse(b.last_down_at ?? b.last_seen_at ?? "") || 0) - (Date.parse(a.last_down_at ?? a.last_seen_at ?? "") || 0) ||
      (a.rx_power_dbm ?? 0) - (b.rx_power_dbm ?? 0));
}

// --- PONs --------------------------------------------------------------------

export type PonTier = "down" | "degraded" | "warn" | "healthy";
export type PonSummary = {
  path: string;
  total: number;
  online: number;
  offline: number;
  weak: number;
  avgRx: number | null;
  worstRx: number | null;
  tier: PonTier;
};

/**
 * One row per PON from its ONTs. On the OLT page any offline ONT marks its
 * PON degraded (this is the troubleshooting view); the wallboard, which must
 * stay quiet at 50 OLTs, only escalates correlated outages.
 */
export function ponSummaries(onts: Pick<ONT, "pon_path" | "run_state" | "rx_power_dbm">[], bands: SignalBands, paths: string[] = []): PonSummary[] {
  const by = new Map<string, Pick<ONT, "pon_path" | "run_state" | "rx_power_dbm">[]>(paths.map((p) => [p, []]));
  for (const o of onts) {
    const k = o.pon_path ?? "?";
    by.set(k, [...(by.get(k) ?? []), o]);
  }
  return [...by.entries()].map(([path, os]) => {
    const online = os.filter((o) => o.run_state === "Online");
    const rx = online.map((o) => o.rx_power_dbm).filter((v): v is number => v !== null && v !== undefined);
    const weak = online.filter((o) => classifyRx(o.rx_power_dbm, bands) === "warn" || classifyRx(o.rx_power_dbm, bands) === "crit").length;
    const offline = os.length - online.length;
    const tier: PonTier = os.length >= 2 && online.length === 0 ? "down" : offline > 0 ? "degraded" : weak > 0 ? "warn" : "healthy";
    return {
      path, total: os.length, online: online.length, offline, weak, tier,
      avgRx: rx.length ? rx.reduce((a, b) => a + b, 0) / rx.length : null,
      worstRx: rx.length ? Math.min(...rx) : null,
    };
  }).sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }));
}

// --- Optics ------------------------------------------------------------------

/** Above this an ONT is comfortably lit; between it and the warn band it is "moderate". */
// ponytail: fixed -20 dBm; make it a setting beside the alert-rule bands if a plant needs another.
export const GOOD_RX = -20;

/** The one optical scale used on every page: wallboard, OLT, PON, ONT list and detail. */
export type RxLevel = "good" | "moderate" | "weak" | "critical" | "nodata";

/**
 * Level for one receive reading. Warn/crit come from the alert rules
 * (useSignalBands), overload above -8 dBm counts as critical; "moderate" is
 * the comfortable-but-not-great band between GOOD_RX and warn.
 */
export function rxLevel(rx: number | null | undefined, bands: SignalBands): RxLevel {
  if (rx === null || rx === undefined || Number.isNaN(rx)) return "nodata";
  const c = classifyRx(rx, bands);
  if (c === "crit") return "critical";
  if (c === "warn") return "weak";
  return rx < GOOD_RX ? "moderate" : "good";
}

export type RxBuckets = { good: number; moderate: number; weak: number; critical: number; noReading: number };

/** Online ONTs by receive level; offline ONTs have no current reading and are counted apart. */
export function rxBuckets(onts: Pick<ONT, "run_state" | "rx_power_dbm">[], bands: SignalBands): RxBuckets {
  const b: RxBuckets = { good: 0, moderate: 0, weak: 0, critical: 0, noReading: 0 };
  for (const o of onts) {
    const rx = o.rx_power_dbm;
    if (o.run_state !== "Online" || rx === null || rx === undefined) { b.noReading++; continue; }
    const l = rxLevel(rx, bands);
    if (l === "nodata") b.noReading++;
    else if (l === "critical") b.critical++;
    else if (l === "weak") b.weak++;
    else b[l]++;
  }
  return b;
}

// --- Telemetry values: never let 0 stand in for "no reading" ----------------

export type Reading =
  | { kind: "value"; value: number; stale: false }
  | { kind: "stale"; last: number | null }
  | { kind: "unavailable" };

/**
 * A metric is a value only when it was reported AND is current. A stale
 * reading is shown as stale (with its last value as context), never as the
 * current number -- a sensor stuck at 0 °C must not read as "0 °C".
 */
export function reading(value: number | null | undefined, stale = false): Reading {
  if (value === null || value === undefined || Number.isNaN(value)) return { kind: "unavailable" };
  if (stale) return { kind: "stale", last: value };
  return { kind: "value", value, stale: false };
}

/** Is a timestamped reading older than three polls? */
export const isStale = (iso: string | null | undefined, pollS: number, nowMs: number) =>
  !!iso && (nowMs - Date.parse(iso)) / 1000 > pollS * STALE_POLLS;

/**
 * The counter row for a PON, if the box reports one. Two spellings: the HSGQ
 * CLI's "PON01".."PON16" (matched on the port number, the path's last
 * segment) and interface names that carry the whole path, as LibreNMS
 * mirrors them ("pon0/0/2", "gpon 0/0/2").
 * ponytail: name-based match; a stored port<->PON link would replace it if a vendor names them otherwise.
 */
export function ponPortFor<P extends { name: string }>(ports: P[], path: string): P | undefined {
  const n = Number(path.split("/").pop());
  return ports.find((p) => {
    const m = /^(?:g?pon|epon)[\s_-]*(?:(\d+\/\d+\/\d+)|0*(\d+))$/i.exec(p.name.trim());
    return !!m && (m[1] === path || (m[2] !== undefined && Number(m[2]) === n));
  });
}
